import { useState } from 'react'
import { Card, Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap'
import playerService from '../services/players'

const emptySkills = {
  staminaSkill: 0,
  keeperSkill: 0,
  defenderSkill: 0,
  playmakerSkill: 0,
  wingerSkill: 0,
  passingSkill: 0,
  scorerSkill: 0,
  setPiecesSkill: 0,
}

const CreateCustomPlayer = ({ oauthToken, onCreate }) => {
  const [showForm, setShowForm] = useState(false)
  const [mode, setMode] = useState('manual')
  const [hattrickId, setHattrickId] = useState('')
  const [loadingTemplate, setLoadingTemplate] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    age: 17,
    ageDays: 0,
    sourcePlayerId: null,
    playerSkills: { ...emptySkills },
  })

  const setSkill = (skillName, value) => {
    setForm(prev => ({
      ...prev,
      playerSkills: {
        ...prev.playerSkills,
        [skillName]: Number(value),
      },
    }))
  }

  const fetchFromHattrick = async () => {
    if (!hattrickId || Number.isNaN(Number(hattrickId))) {
      setError('Anna kelvollinen Hattrick-pelaaja ID')
      return
    }

    setError('')
    setInfo('')
    setLoadingTemplate(true)

    try {
      const template = await playerService.getCustomTemplate(
        hattrickId,
        oauthToken
      )
      let skills = { ...emptySkills }
      if (template.playerSkills) {
        skills = {
          staminaSkill: template.playerSkills.staminaSkill ?? 0,
          keeperSkill: template.playerSkills.keeperSkill ?? 0,
          defenderSkill: template.playerSkills.defenderSkill ?? 0,
          playmakerSkill: template.playerSkills.playmakerSkill ?? 0,
          wingerSkill: template.playerSkills.wingerSkill ?? 0,
          passingSkill: template.playerSkills.passingSkill ?? 0,
          scorerSkill: template.playerSkills.scorerSkill ?? 0,
          setPiecesSkill: template.playerSkills.setPiecesSkill ?? 0,
        }
      }

      setForm({
        firstName: template.firstName || '',
        lastName: template.lastName || '',
        age: template.age || 17,
        ageDays: template.ageDays || 0,
        sourcePlayerId: template.sourcePlayerId || Number(hattrickId),
        playerSkills: skills,
      })

      if (!template.hasVisibleSkills) {
        setInfo('Taitoja ei näkynyt Hattrickista. Syötä taidot itse.')
      } else {
        setInfo('Pohjatiedot haettu Hattrickista.')
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Pohjatietojen haku epäonnistui')
    } finally {
      setLoadingTemplate(false)
    }
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Anna pelaajan etu- ja sukunimi')
      return
    }

    setError('')
    setInfo('')
    setSaving(true)

    try {
      const payload = {
        ...form,
        age: Number(form.age),
        ageDays: Number(form.ageDays),
      }
      const created = await onCreate(payload)
      setInfo(`Pelaaja ${created.firstName} ${created.lastName} luotu`)
      setHattrickId('')
      setMode('manual')
      setForm({
        firstName: '',
        lastName: '',
        age: 17,
        ageDays: 0,
        sourcePlayerId: null,
        playerSkills: { ...emptySkills },
      })
    } catch (err) {
      setError(err?.response?.data?.error || 'Pelaajan luonti epäonnistui')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      style={{
        marginTop: '20px',
        marginBottom: '30px',
        backgroundColor: '#2C2F33',
        borderColor: '#1E2124',
        color: '#FFFFFF',
      }}
    >
      <Card.Header
        as="h5"
        style={{
          backgroundColor: '#23272A',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>Luo pelaaja</span>
        <Button
          variant="outline-light"
          size="sm"
          onClick={() => setShowForm(prev => !prev)}
        >
          {showForm ? 'Piilota' : 'Näytä'}
        </Button>
      </Card.Header>
      {showForm && (
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {info && <Alert variant="info">{info}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Check
                  inline
                  type="radio"
                  id="create-manual"
                  name="createMode"
                  label="Täysin omilla tiedoilla"
                  checked={mode === 'manual'}
                  onChange={() => setMode('manual')}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="create-hattrick"
                  name="createMode"
                  label="Hae pohjatiedot Hattrickista"
                  checked={mode === 'hattrick'}
                  onChange={() => setMode('hattrick')}
                />
              </Col>
            </Row>

            {mode === 'hattrick' && (
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Control
                    type="number"
                    placeholder="Hattrick Player ID"
                    value={hattrickId}
                    onChange={e => setHattrickId(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Button
                    variant="secondary"
                    onClick={fetchFromHattrick}
                    disabled={loadingTemplate}
                  >
                    {loadingTemplate ? (
                      <>
                        <Spinner animation="border" size="sm" /> Haetaan...
                      </>
                    ) : (
                      'Hae pohjatiedot'
                    )}
                  </Button>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Etunimi</Form.Label>
                <Form.Control
                  value={form.firstName}
                  onChange={e =>
                    setForm({ ...form, firstName: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Form.Label>Sukunimi</Form.Label>
                <Form.Control
                  value={form.lastName}
                  onChange={e => setForm({ ...form, lastName: e.target.value })}
                />
              </Col>
              <Col md={2}>
                <Form.Label>Ikä (vuodet)</Form.Label>
                <Form.Control
                  type="number"
                  min="15"
                  max="40"
                  value={form.age}
                  onChange={e => setForm({ ...form, age: e.target.value })}
                />
              </Col>
              <Col md={2}>
                <Form.Label>Ikä (päivät)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="111"
                  value={form.ageDays}
                  onChange={e => setForm({ ...form, ageDays: e.target.value })}
                />
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={3}>
                <Form.Label>Maalivahti</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={form.playerSkills.keeperSkill}
                  onChange={e => setSkill('keeperSkill', e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Puolustus</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={form.playerSkills.defenderSkill}
                  onChange={e => setSkill('defenderSkill', e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Pelinrakennus</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={form.playerSkills.playmakerSkill}
                  onChange={e => setSkill('playmakerSkill', e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Laituri</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={form.playerSkills.wingerSkill}
                  onChange={e => setSkill('wingerSkill', e.target.value)}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Syöttäminen</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={form.playerSkills.passingSkill}
                  onChange={e => setSkill('passingSkill', e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Maalinteko</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={form.playerSkills.scorerSkill}
                  onChange={e => setSkill('scorerSkill', e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Erikoistilanteet</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={form.playerSkills.setPiecesSkill}
                  onChange={e => setSkill('setPiecesSkill', e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Kestävyys</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={form.playerSkills.staminaSkill}
                  onChange={e => setSkill('staminaSkill', e.target.value)}
                />
              </Col>
            </Row>

            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Tallennetaan...' : 'Luo pelaaja'}
            </Button>
          </Form>
        </Card.Body>
      )}
    </Card>
  )
}

export default CreateCustomPlayer
